import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

/**
 * Once per user (localStorage): after the intro auto-open has run OR the user
 * opened Beacon manually, we never auto-open again — avoids popups every new
 * browser session (e.g. after a day away). Session-only keys reset too often.
 */
const BEACON_INTRO_SHOWN_PREFIX = "beacon_intro_shown_v1_";
/** Persists conversation across navigation (e.g. Explore) until tab closes */
const CHAT_MESSAGES_STORAGE_KEY = "beacon_chat_messages_v1";
const AUTO_OPEN_DELAY_MS = 3500;
/** Show typing dots after API returns, before the answer appears (~couple of seconds) */
const TYPING_BEFORE_REVEAL_MS = 2200;
/** Typewriter: ms between character batches (smaller = faster) */
const TYPEWRITER_TICK_MS = 22;
const TYPEWRITER_CHARS_PER_TICK = 2;
const CHATBOT_NAME = "Beacon";
const WELCOME_MESSAGE = `Hi — I'm Beacon. I can help with donations, projects, your dashboard, monitoring, receipts, fees, and anything else about Philanthrify.

What would you like help with today?`;

const LIVE_SUPPORT_PHRASES = [
  "talk to agent", "live support", "talk to team", "human", "real person",
  "contact sales", "speak to someone", "talk to someone", "live agent",
  "customer support", "support team", "connect me", "transfer to agent",
];

function isLiveSupportIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  return LIVE_SUPPORT_PHRASES.some((p) => lower.includes(p));
}

function readBeaconIntroShown(userId: number): boolean {
  if (typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem(`${BEACON_INTRO_SHOWN_PREFIX}${userId}`) === "1";
  } catch {
    return false;
  }
}

function writeBeaconIntroShown(userId: number) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(`${BEACON_INTRO_SHOWN_PREFIX}${userId}`, "1");
  } catch {
    // ignore quota / private mode
  }
}

type ChatAction = { label: string; path: string };
type Message = { role: "user" | "assistant"; content: string; actions?: ChatAction[] };

/**
 * WhatsApp-style typing: compact incoming bubble + three dots with staggered bounce.
 */
function WhatsAppTypingBubble({ isDark }: { isDark: boolean }) {
  const dotColor = "#8696a0";
  return (
    <>
      <style>{`
        @keyframes chatbotWaTypingDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.45; }
          30% { transform: translateY(-5px); opacity: 1; }
        }
        .chatbot-wa-typing-dot {
          animation: chatbotWaTypingDot 1.05s ease-in-out infinite;
        }
      `}</style>
      <div
        className="inline-flex min-h-[36px] items-center rounded-[19px] rounded-bl-[5px] px-[16px] py-[10px]"
        style={{
          backgroundColor: isDark ? "#2a3942" : "#e8e8e8",
          boxShadow: isDark ? "none" : "0 1px 0.5px rgba(11, 20, 26, 0.12)",
        }}
        role="status"
        aria-label="Beacon is typing"
      >
        <span className="inline-flex items-center gap-[5px]" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="chatbot-wa-typing-dot h-2 w-2 shrink-0 rounded-full"
              style={{
                backgroundColor: dotColor,
                animationDelay: `${i * 0.18}s`,
              }}
            />
          ))}
        </span>
      </div>
    </>
  );
}

/** Bot icon (svgrepo bot) - for Beacon avatar */
function BotIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path
        fillRule="nonzero"
        d="M17.7530511,13.999921 C18.9956918,13.999921 20.0030511,15.0072804 20.0030511,16.249921 L20.0030511,17.1550008 C20.0030511,18.2486786 19.5255957,19.2878579 18.6957793,20.0002733 C17.1303315,21.344244 14.8899962,22.0010712 12,22.0010712 C9.11050247,22.0010712 6.87168436,21.3444691 5.30881727,20.0007885 C4.48019625,19.2883988 4.00354153,18.2500002 4.00354153,17.1572408 L4.00354153,16.249921 C4.00354153,15.0072804 5.01090084,13.999921 6.25354153,13.999921 L17.7530511,13.999921 Z M11.8985607,2.00734093 L12.0003312,2.00049432 C12.380027,2.00049432 12.6938222,2.2826482 12.7434846,2.64872376 L12.7503312,2.75049432 L12.7495415,3.49949432 L16.25,3.5 C17.4926407,3.5 18.5,4.50735931 18.5,5.75 L18.5,10.254591 C18.5,11.4972317 17.4926407,12.504591 16.25,12.504591 L7.75,12.504591 C6.50735931,12.504591 5.5,11.4972317 5.5,10.254591 L5.5,5.75 C5.5,4.50735931 6.50735931,3.5 7.75,3.5 L11.2495415,3.49949432 L11.2503312,2.75049432 C11.2503312,2.37079855 11.5324851,2.05700336 11.8985607,2.00734093 L12.0003312,2.00049432 L11.8985607,2.00734093 Z M9.74928905,6.5 C9.05932576,6.5 8.5,7.05932576 8.5,7.74928905 C8.5,8.43925235 9.05932576,8.99857811 9.74928905,8.99857811 C10.4392523,8.99857811 10.9985781,8.43925235 10.9985781,7.74928905 C10.9985781,7.05932576 10.4392523,6.5 9.74928905,6.5 Z M14.2420255,6.5 C13.5520622,6.5 12.9927364,7.05932576 12.9927364,7.74928905 C12.9927364,8.43925235 13.5520622,8.99857811 14.2420255,8.99857811 C14.9319888,8.99857811 15.4913145,8.43925235 15.4913145,7.74928905 C15.4913145,7.05932576 14.9319888,6.5 14.2420255,6.5 Z"
      />
    </svg>
  );
}

/** Chat icon (svgrepo chat) - floating access button */
function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 400 400" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M197.737 285.624C290.514 285.624 344.323 248.092 359.164 173.028C372.581 106.263 318.874 79.5184 241.496 69.053C164.118 58.5877 63.6024 63.7698 42.5718 141.053C21.5412 218.336 99.5252 252.471 116.53 256.928C133.534 261.384 123.894 334.854 132.4 334.854C140.905 334.854 163.679 290.34 173.755 285.624" />
    </svg>
  );
}

/** User avatar icon */
function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function Chatbot() {
  const { darkMode } = useSelector((state: RootState) => state.darkMode);
  const userSelection = useSelector((state: RootState) => state.user?.userSelection ?? null);
  const auth = useSelector((state: RootState) => state.auth);
  const token = auth?.token ?? null;
  const isDonorView = userSelection === "DONOR" || userSelection === "DONORORG";
  const isGrantHubSurface =
    typeof window !== "undefined" &&
    (window.location.pathname === "/grant-hub" ||
      window.location.pathname.startsWith("/grant-hub/") ||
      window.location.pathname === "/grants-hub" ||
      window.location.pathname.startsWith("/grants-hub/") ||
      (window.location.pathname === "/dashboard-charity" &&
        new URLSearchParams(window.location.search).get("section") === "grants"));
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  /** After API returns: show typing dots before streaming the reply */
  const [pendingReveal, setPendingReveal] = useState<{ reply: string; actions?: ChatAction[] } | null>(null);
  /** Typewriter state for assistant message at index */
  const [streamJob, setStreamJob] = useState<{
    index: number;
    full: string;
    actions?: ChatAction[];
    pos: number;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (open) scrollToBottom();
  }, [open, messages, loading, pendingReveal, streamJob, scrollToBottom]);

  /** After API success: brief typing, then empty assistant bubble + typewriter */
  useEffect(() => {
    if (!pendingReveal) return;
    const buf = pendingReveal;
    revealTimerRef.current = setTimeout(() => {
      setPendingReveal(null);
      setMessages((prev) => {
        const idx = prev.length;
        queueMicrotask(() =>
          setStreamJob({
            index: idx,
            full: buf.reply,
            actions: buf.actions,
            pos: 0,
          }),
        );
        return [...prev, { role: "assistant", content: "" }];
      });
    }, TYPING_BEFORE_REVEAL_MS);
    return () => {
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    };
  }, [pendingReveal]);

  /** Typewriter: reveal text in chunks, then attach actions */
  useEffect(() => {
    if (!streamJob) return;
    const { index, full, actions, pos } = streamJob;
    if (pos >= full.length) {
      setMessages((prev) => {
        const next = [...prev];
        if (next[index]) {
          next[index] = { ...next[index], content: full, actions };
        }
        return next;
      });
      setStreamJob(null);
      return;
    }
    const id = window.setTimeout(() => {
      const nextPos = Math.min(full.length, pos + TYPEWRITER_CHARS_PER_TICK);
      setMessages((prev) => {
        const next = [...prev];
        if (next[index]) {
          next[index] = { ...next[index], content: full.slice(0, nextPos) };
        }
        return next;
      });
      setStreamJob({ index, full, actions, pos: nextPos });
    }, TYPEWRITER_TICK_MS);
    return () => clearTimeout(id);
  }, [streamJob]);

  /** Restore messages after navigation; keep conversation when visiting Explore etc. */
  useEffect(() => {
    if (!token || typeof sessionStorage === "undefined") {
      setHydrated(true);
      return;
    }
    try {
      const raw = sessionStorage.getItem(CHAT_MESSAGES_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { messages?: Message[] };
        if (Array.isArray(parsed.messages) && parsed.messages.length > 0) {
          setMessages(parsed.messages);
          setHydrated(true);
          return;
        }
      }
    } catch {
      // ignore
    }
    setHydrated(true);
  }, [token]);

  /** Persist transcript */
  useEffect(() => {
    if (!token || typeof sessionStorage === "undefined" || !hydrated) return;
    if (messages.length === 0) return;
    try {
      sessionStorage.setItem(CHAT_MESSAGES_STORAGE_KEY, JSON.stringify({ messages }));
    } catch {
      // ignore
    }
  }, [messages, token, hydrated]);

  /**
   * First visit only: auto-open once per user (localStorage), not every new
   * browser session. Skip if there is already a saved chat thread.
   */
  useEffect(() => {
    if (!token || !hydrated || typeof sessionStorage === "undefined") return;
    const userId = auth?.userId;
    if (userId == null) return;

    try {
      const raw = sessionStorage.getItem(CHAT_MESSAGES_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { messages?: Message[] };
        if (Array.isArray(parsed.messages) && parsed.messages.length > 0) return;
      }
    } catch {
      // ignore
    }

    if (readBeaconIntroShown(userId)) return;

    if (autoOpenTimerRef.current) {
      clearTimeout(autoOpenTimerRef.current);
      autoOpenTimerRef.current = null;
    }
    autoOpenTimerRef.current = setTimeout(() => {
      autoOpenTimerRef.current = null;
      writeBeaconIntroShown(userId);
      setOpen(true);
      setMessages([{ role: "assistant", content: WELCOME_MESSAGE }]);
    }, AUTO_OPEN_DELAY_MS);
    return () => {
      if (autoOpenTimerRef.current) {
        clearTimeout(autoOpenTimerRef.current);
        autoOpenTimerRef.current = null;
      }
    };
  }, [token, hydrated, auth?.userId]);

  const notifySupportTeam = useCallback(async (userMessage: string) => {
    const baseUrl =
      typeof window !== "undefined" && (window as unknown as { API_URL?: string }).API_URL
        ? (window as unknown as { API_URL: string }).API_URL
        : "";
    const url = `${baseUrl.replace(/\/$/, "")}/chat/request-agent`;
    const userEmail = auth?.email ?? "";
    try {
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userMessage, userEmail }),
      });
    } catch {
      // ignore
    }
  }, [auth]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading || pendingReveal || streamJob) return;

    const userMessage: Message = { role: "user", content: text };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    const baseUrl =
      typeof window !== "undefined" && (window as unknown as { API_URL?: string }).API_URL
        ? (window as unknown as { API_URL: string }).API_URL
        : "";
    const url = `${baseUrl.replace(/\/$/, "")}/chat/message`;

    try {
      const conversation = [...messages, userMessage].map((m) => ({ role: m.role, content: m.content }));
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers.Authorization = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          messages: conversation,
          userRole: userSelection ?? undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      const reply =
        typeof data.reply === "string"
          ? data.reply
          : typeof data.error === "string"
            ? data.error
            : !res.ok
              ? "Couldn't get a response. Try again."
              : "Try: How do I donate? Where's my receipt?";
      const actions =
        Array.isArray(data.actions) &&
        data.actions.every(
          (a: unknown) =>
            a &&
            typeof a === "object" &&
            typeof (a as ChatAction).label === "string" &&
            typeof (a as ChatAction).path === "string",
        )
          ? (data.actions as ChatAction[])
          : undefined;

      setLoading(false);
      setPendingReveal({ reply, actions });

      if (isLiveSupportIntent(text)) {
        notifySupportTeam(text);
      }
    } catch {
      setLoading(false);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Connection issue. Check your network and try again." },
      ]);
    }
  }, [input, loading, pendingReveal, streamJob, messages, notifySupportTeam, token, userSelection]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isDark = !!darkMode;
  const accentBg = isDonorView ? "bg-tertiaryColor" : "bg-primaryColor";
  const accentRing = isDonorView ? "focus:ring-tertiaryColor" : "focus:ring-primaryColor";
  const accentBorder = isDonorView ? "focus:border-tertiaryColor" : "focus:border-primaryColor";
  const textPrimary = isDark ? "text-white" : "text-slate-900";
  const textSecondary = isDark ? "text-slate-400" : "text-slate-500";
  const userAvatarBg = isDark ? "bg-slate-500/70" : "bg-slate-400/80";

  // Same glass as Transaction proof (BlockchainTransactionModal): translucent panel + inner cards
  const panelGlassStyle: React.CSSProperties = {
    backgroundColor: isDark ? "rgba(25, 28, 40, 0.6)" : "rgba(245, 245, 250, 0.5)",
    backdropFilter: "blur(50px) saturate(200%) brightness(1.1)",
    WebkitBackdropFilter: "blur(50px) saturate(200%) brightness(1.1)",
    border: isDark ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid rgba(255, 255, 255, 0.9)",
    boxShadow: isDark
      ? "0 50px 120px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.15)"
      : "0 50px 120px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 1), inset 0 -1px 0 rgba(255, 255, 255, 0.8)",
  };
  const cardGlassStyle: React.CSSProperties = {
    backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 0.75)",
    backdropFilter: "blur(25px) brightness(1.05)",
    WebkitBackdropFilter: "blur(25px) brightness(1.05)",
    border: `1.5px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 255, 255, 0.9)"}`,
    boxShadow: isDark
      ? "0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
      : "0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 1)",
  };

  /** Beacon only for signed-in users (donor vs charity experience stays consistent) */
  if (!token || isGrantHubSurface) {
    return null;
  }

  return (
    <>
      <style>{`
        body.transaction-mobile-modal-open .beacon-chatbot-launcher,
        body.transaction-mobile-modal-open .beacon-chatbot-panel,
        body.transaction-group-mobile-modal-open .beacon-chatbot-launcher,
        body.transaction-group-mobile-modal-open .beacon-chatbot-panel,
        body.nested-transaction-drawer-open .beacon-chatbot-launcher,
        body.nested-transaction-drawer-open .beacon-chatbot-panel {
          display: none !important;
        }
      `}</style>
      <button
        type="button"
        onClick={() => {
          if (!open) {
            if (autoOpenTimerRef.current) {
              clearTimeout(autoOpenTimerRef.current);
              autoOpenTimerRef.current = null;
            }
            const uid = auth?.userId;
            if (uid != null) writeBeaconIntroShown(uid);
            if (messages.length === 0) {
              setMessages([{ role: "assistant", content: WELCOME_MESSAGE }]);
            }
          }
          setOpen((o) => !o);
        }}
        className={`beacon-chatbot-launcher fixed bottom-6 right-6 z-[9999] flex h-12 w-12 items-center justify-center rounded-full border border-white/25 shadow-[0_8px_32px_rgba(0,0,0,0.15)] transition-all hover:scale-105 hover:shadow-[0_12px_40px_rgba(0,0,0,0.2)] focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-transparent ${accentRing} ${accentBg}`}
        aria-label={`Chat with ${CHATBOT_NAME}`}
      >
        <ChatIcon className="h-6 w-6 text-white" />
      </button>

      {open && (
        <div
          className="beacon-chatbot-panel fixed bottom-24 right-6 z-[9998] flex h-[500px] w-[400px] max-h-[88vh] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-[28px] sm:rounded-[32px]"
          style={panelGlassStyle}
          role="dialog"
          aria-label={`Chat with ${CHATBOT_NAME}`}
        >
          {/* Header bar (same treatment as Transaction proof top bar) */}
          <div className="flex shrink-0 items-center justify-between px-4 py-3 rounded-t-[28px] sm:rounded-t-[32px] border-b border-black/[0.06] dark:border-white/[0.08]">
            <div className="flex min-w-0 items-center gap-3">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white ${accentBg}`}>
                <BotIcon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className={`truncate text-[11px] opacity-60 ${textSecondary}`}>Platform assistant</p>
                <p className={`flex items-center gap-2 truncate text-base font-semibold ${textPrimary}`}>
                  {CHATBOT_NAME}
                  <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400 opacity-80" aria-hidden />
                  <span className="text-xs font-normal opacity-60">Online</span>
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className={`shrink-0 rounded-xl p-2 opacity-70 transition-opacity hover:opacity-100 ${textSecondary}`}
              aria-label="Close chat"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages: text in glass bubble; quick links separate, minimal */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : "flex-row"}`}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white ${
                    m.role === "user" ? userAvatarBg : accentBg
                  }`}
                >
                  {m.role === "user" ? (
                    <UserIcon className="h-4 w-4" />
                  ) : (
                    <BotIcon className="h-4 w-4" />
                  )}
                </div>
                {m.role === "user" ? (
                  <div
                    className={`max-w-[80%] rounded-2xl px-5 py-3.5 text-[15px] leading-relaxed text-white rounded-br-md ${accentBg}`}
                  >
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  </div>
                ) : (
                  <div className="flex min-w-0 flex-1 flex-col gap-2.5">
                    <div
                      className={`w-fit max-w-full rounded-2xl rounded-bl-md px-5 py-3.5 text-[15px] leading-relaxed ${textPrimary}`}
                      style={cardGlassStyle}
                    >
                      <div className="whitespace-pre-wrap">{m.content}</div>
                    </div>
                    {m.actions && m.actions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pl-0.5">
                        {m.actions.map((a, ai) => (
                          <a
                            key={`${a.path}-${ai}`}
                            href={a.path}
                            className={`inline-flex items-center rounded-lg border px-2.5 py-1.5 text-[11px] font-medium tracking-wide transition-colors ${
                              isDark
                                ? "border-white/15 text-white/85 hover:bg-white/10"
                                : "border-black/10 text-slate-700 hover:bg-black/[0.04]"
                            }`}
                          >
                            {a.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {(loading || pendingReveal) && (
              <div className="flex items-end gap-2">
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white ${accentBg}`}>
                  <BotIcon className="h-4 w-4" />
                </div>
                <WhatsAppTypingBubble isDark={isDark} />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input: same card glass as Transaction proof */}
          <div className="shrink-0 border-t border-black/[0.06] dark:border-white/[0.08] px-4 py-4">
            <div className="flex gap-3">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Beacon..."
                rows={1}
                className={`flex-1 resize-none rounded-2xl px-4 py-3 text-[15px] leading-relaxed placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-0 ${accentRing} ${accentBorder} ${textPrimary}`}
                style={cardGlassStyle}
                disabled={loading || !!pendingReveal || !!streamJob}
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={!input.trim() || loading || !!pendingReveal || !!streamJob}
                className={`shrink-0 rounded-2xl px-4 py-3 text-white transition-opacity hover:opacity-90 disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-white/40 ${accentBg} ${accentRing}`}
                aria-label="Send"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Chatbot;
